<template>
  <v-dialog v-model="open" max-width="700px" persistent>
    <template v-slot:activator="{ on, attrs }">
      <location-info-item
          icon="mdi-label-multiple-outline"
          icon-action="mdi-pencil-outline"
          title="Atributos"
          v-bind="attrs"
          v-on="on"
      >
        <template slot:subtitle>
          <v-list-item class="ml-0 pl-0" v-for="(itens, groupName) in itemsForResume" :key="groupName">
            <v-list-item-content class="py-1">
              <v-list-item-title class="pt-1">{{ groupName }}</v-list-item-title>
              <div v-for="(item, index) in itens" :key="index">
                <v-list-item-subtitle>{{ item.display_name }}{{ item.value_type === 'ENUM' ? ' - ' + getEnumDisplayValue(item) : '' }}</v-list-item-subtitle>

                <div v-if="item.value_type === 'REPEATED_ENUM'">
                  <v-list-item-subtitle
                      class="ml-4"
                      v-for="(subItem, index) in getRepeatedEnumItems(item)"
                      :key="index"
                  >
                    {{ subItem.displayName }} - {{ subItem.isSet ? 'Aceita' : subItem.isUnset ? 'Não aceita' : ''  }}
                  </v-list-item-subtitle>
                </div>
              </div>
            </v-list-item-content>
          </v-list-item>
        </template>
      </location-info-item>
    </template>

    <v-card>
      <v-toolbar elevation="0">
        <v-card-title class="ml-0 pl-1">
          Atributos
        </v-card-title>

        <v-spacer />

        <v-btn icon @click="open = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text>
        Exiba atributos no Perfil da sua empresa para que os clientes saibam mais sobre ela. Eles podem aparecer publicamente na Pesquisa, no Maps e em outros Serviços do Google.
        <a class="my-link" target="_blank" href="https://support.google.com/business/answer/9049526">Saiba mais</a>
      </v-card-text>

      <div class="ml-2 px-4 mt-2 input-search">
        <v-text-field
          full-width
          dense
          outlined
          clearable
          placeholder="Buscar atributos"
          prepend-inner-icon="mdi-magnify"
          @keyup="event => search(event.target.value)"
          @change="search"
          v-on:keyup.enter="() => callAction()"
        />
      </div>

      <div class="px-4">
        <div v-for="(attrs, groupName) in availableAttributes" :key="groupName">
          <div
            class="text-subtitle-1 ml-2 mb-0 mt-4"
            v-if="attrs.filter(i => i.value_type === 'BOOL').length > 0
            || attrs.filter(i => i.value_type === 'REPEATED_ENUM').length > 0
            || attrs.filter(i => i.value_type === 'ENUM').length > 0"
          >
            {{ groupName }}
          </div>

          <!--Bool-->
          <location-attribute-switch
            class="ml-2"
            v-for="item in attrs.filter(i => i.value_type === 'BOOL')"
            :key="item.id"
            :text="item.display_name"
            :value="get(item, 'current_value.pivot.values', item.value)"
            v-on:change="val => changeData(val, item.attribute_id, 'BOOL')"
          />

          <!--Repeated Enum-->
          <location-attribute-select-multiple
            v-for="item in attrs.filter(i => i.value_type === 'REPEATED_ENUM')"
            :key="item.id"
            :title="item.display_name"
            :items="item.value_metadata.map(i => ({
              display_name: i.displayName,
              value_type: 'BOOL',
              item_value: i.value,
              value: getValueInnerAttribute(item, i.value),
              attribute_id: item.attribute_id
            }))"
            v-on:change="val => changeData(val, item.attribute_id, 'REPEATED_ENUM')"
          />

          <!--Enum-->
          <location-attribute-select
            v-for="item in attrs.filter(i => i.value_type === 'ENUM')"
            :key="item.id"
            :title="item.display_name"
            :items="item.value_metadata.map(i => ({
              display_name: i.displayName,
              value_type: 'BOOL',
              value: i.value,
              item_value: i.value,
              attribute_id: item.attribute_id
            }))"
            :value="getEnumValue(item)"
            v-on:change="val => changeData(val, item.attribute_id, 'ENUM')"
          />
        </div>
      </div>

      <v-card-text>
        <v-form
            @submit="e => e.preventDefault()"
            ref="form"
            v-model="formValid"
            lazy-validation
        >

          <v-alert
            v-if="alertScheduledTask"
            dense
            border="right"
            type="warning"
          >
            Existe um agendamento pendente para essa informação
          </v-alert>

          <schedule-form  v-if="!bulkUpdate" v-on:change="changeScheduleDate" />
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
            color="secondary"
            text
            @click="cancel"
        >
          Cancelar
        </v-btn>
        <v-btn
            :loading="loading"
            color="primary"
            text
            @click="callAction"
        >
          Aplicar
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
import LocationInfoItem from '@/views/Locations/Components/LocationInfoItem'
import {mapState} from 'vuex'
import {get, groupBy} from 'lodash'
import {TYPE_UPDATE_LOCATION_ATTRIBUTES} from '@/constants/tasks'
import ScheduleForm from '@/views/Locations/Components/ScheduleForm'
import LocationAttributeSwitch from '@/views/Locations/Components/LocationAttributeSwitch'
import LocationAttributeSelectMultiple from '@/views/Locations/Components/LocationAttributeSelectMultiple'
import LocationAttributeSelect from '@/views/Locations/Components/LocationAttributeSelect'

export default {
  name: "LocationAttributesModal",
  props: {
    action: Function,
    bulkUpdate: {
      type: Boolean,
    },
    attrs: Object,
    alertScheduleTask: Array,
  },
  components: {
    LocationAttributeSelectMultiple,
    LocationAttributeSelect,
    LocationInfoItem,
    ScheduleForm,
    LocationAttributeSwitch
  },
  computed: {
    ...mapState({
      location: state => state.locations.location
    }),
    get() {
      return get
    }
  },
  beforeDestroy() {
    this.resetData()
  },
  mounted() {
    this.availableAttributes = this.attrs || {}
    if (this.location) {
      this.setData(this.location)
    }
  },
  data: () => ({
    open: false,
    loading: false,
    formValid: true,
    formData: {
      schedule_type: TYPE_UPDATE_LOCATION_ATTRIBUTES,
      attributes: [],
      schedule_start: '',
      schedule_start_at: '',
      schedule_end: '',
      schedule_end_at: '',
    },
    availableAttributes: [],
    allAttributes: [],
    itemsForResume: [],
    alertScheduledTask: null
  }),
  methods: {
    resumeItems() {
      const attributes = get(this.location, 'data.attributes')
      if (attributes) {
        this.itemsForResume = groupBy(attributes.filter(a => a.value_type !== 'URL'), (a) => a.group_display_name)
      }
    },
    search(value) {
      const term = (value || '').toLowerCase()
      if (term === '') {
        this.availableAttributes = JSON.parse(JSON.stringify(this.allAttributes))
        return
      }
      let items = {}
      for (const groupName in this.allAttributes) {
        let find = this.allAttributes[groupName].filter(a => a.display_name.toLowerCase().includes(term))
        if (find.length > 0) {
          items[groupName] = Array.isArray(items[groupName]) ? items[groupName] : []
          items[groupName] = items[groupName].concat(find)
        }
      }
      this.availableAttributes = items
    },
    cancel() {
      this.open = false
      this.resetData()
    },
    resetData() {
      this.allAttributes = get(location, 'data.available_attributes')
      this.availableAttributes = get(location, 'data.available_attributes')
      if (this.bulkUpdate) {
        this.$emit('removePayload', 'attributes')
      }
    },
    callAction: async function () {
      this.validate()
      if (!this.formValid) {
        return
      }

      if(this.bulkUpdate){
        this.$emit('savePayload', 'attributes', this.formData.attributes)
      } else if (this.action) {
        this.loading = true
        await this.action(this.formData)
        this.loading = false
      }
      this.open = false
    },
    validate() {
      return this.$refs.form.validate()
    },
    changeScheduleDate(data) {
      this.formData.schedule_start = data.schedule_start
      this.formData.schedule_start_at = data.schedule_start_at
      this.formData.schedule_end = data.schedule_end
      this.formData.schedule_end_at = data.schedule_end_at
    },
    getValueInnerAttribute(item, value) {
      let setValues = get(item, 'current_value.pivot.repeated_enum_value.setValues', [])
      setValues = Array.isArray(setValues) ? setValues : []
      let unsetValues = get(item, 'current_value.pivot.repeated_enum_value.unsetValues', [])
      unsetValues = Array.isArray(unsetValues) ? unsetValues : []

      return setValues.includes(value)
          ? true
          : unsetValues.includes(value)
              ? false
              : null
    },
    getEnumValue(item) {
      return get(item, 'current_value.pivot.values[0]', '')
    },
    changeData(value, attribute_id) {
      const exists = this.formData.attributes.findIndex(i => i.attribute_id === attribute_id)
      if (exists > -1) {
        this.formData.attributes[exists].value = value
      } else {
        this.formData.attributes.push({ attribute_id, value })
      }
    },
    getAllAttributes(location) {
      return this.attrs || get(location, 'data.available_attributes')
    },
    setData(location) {
      this.availableAttributes = this.getAllAttributes(location)
      this.allAttributes = this.getAllAttributes(location)
      this.resumeItems()
    },
    getEnumDisplayValue(data) {
      const pivotValues = get(data, 'pivot.values')
      if (!pivotValues || !data.value_metadata || !Array.isArray(pivotValues)) {
        return
      }
      const key = data.pivot.values[0]
      const value = data.value_metadata.find(i => i.value === key)
      return value.displayName
    },
    getRepeatedEnumItems(data) {
      const repeatedValue = get(data, 'pivot.repeated_enum_value')
      if (!repeatedValue) {
        return
      }
      const setValues = get(data, 'pivot.repeated_enum_value.setValues')
      const unsetValues = get(data, 'pivot.repeated_enum_value.unsetValues')
      if (!setValues || !unsetValues) {
        return
      }

      const allValues = [ ...setValues, ...unsetValues ]
      const metadata = data.value_metadata.filter(m => allValues.findIndex(val => val === m.value) > -1)
      return metadata.map(i => {
        const isUnset = unsetValues.findIndex(u => u === i.value) > -1
        const isSet = setValues.findIndex(u => u === i.value) > -1
        return {
          ...i,
          isUnset,
          isSet
        }
      })
    }
  },
  watch: {
    location(location) {
      this.setData(location)
    },
    open(open) {
      if (open) {
        this.setData(this.location)
      }
    }
  }
}
</script>

<style scoped>
.my-link {
  text-decoration: none;
}
.my-link:hover {
  text-decoration: underline;
}
.input-search {
  max-width: 400px;
}
</style>